import { Component, ErrorHandler, Input, Injector, OnInit, ChangeDetectionStrategy, inject, signal, untracked } from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { disabled, FieldTree, form, required } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'

import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { KirjautunutKayttajaService } from '../../_angular/service/kirjautunut-kayttaja.service'
import { arvonMuutokset, asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { TimestampService } from '../../_jaettu-angular/service/timestamp-service'

import { Ajopaivakirja } from '@lemon/core-firebase/model/kayttaja'

import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { Subject } from 'rxjs'
import { map, takeUntil, tap } from 'rxjs/operators'

interface AjopaivakirjaLomake {
  kokonaisKmMaara: number | null
  tyoAjoissaKm: number | null
  tiedotPerustuvat: Ajopaivakirja['tiedotPerustuvat'] | null
  yli10hKotimaanMatkojenLukumaara: number | null
  yli6hKotimaanMatkojenLukumaara: number | null
  ulkomaanMatkapaivienLukumaara: number | null
  ulkomaanPaivarahojenYhteismaara: number | null
  kotiYrityksenTyotilana: Ajopaivakirja['kotiYrityksenTyotilana'] | null
}

@Component({
  selector: '[app-ajopaivakirja-lemonator]',
  templateUrl: './ajopaivakirja-lemonator.component.html',
  styleUrls: ['./ajopaivakirja-lemonator.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AjopaivakirjaLemonatorComponent implements OnInit {
  @Input() asiakasAvain: string

  private ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)
  private readonly _tiedotPerustuvatPoissa = signal(true)

  lomake: FieldTree<AjopaivakirjaLomake>
  readonly commonError = signal<string>(undefined)

  paasaantoinenKotiVahennys: string = '920'
  osaaikainenKotiVahennys: string = '460'
  satunnainenKotiVahennys: string = '230'

  private _saveStarted: boolean
  private _ajopaivakirja: Ajopaivakirja

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _copyService: KopioijaPalvelu,
    private _ladataanService: LadataanService,
    private _validationService: FormValidationService,
    private _snackbar: MatSnackBar,
    private _translationService: LemonTranslationService,
    private _timestampService: TimestampService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _errorHandler: ErrorHandler,
  ) {}

  ngOnInit() {
    this.lomake = form(
      signal<AjopaivakirjaLomake>({
        kokonaisKmMaara: null,
        tyoAjoissaKm: null,
        tiedotPerustuvat: null,
        yli10hKotimaanMatkojenLukumaara: null,
        yli6hKotimaanMatkojenLukumaara: null,
        ulkomaanMatkapaivienLukumaara: null,
        ulkomaanPaivarahojenYhteismaara: null,
        kotiYrityksenTyotilana: null,
      }),
      polku => {
        validoiReaktiivisesti(polku.tyoAjoissaKm, this.tooLargeTyoAjoissaKmValidator)
        disabled(polku.tiedotPerustuvat, () => this._tiedotPerustuvatPoissa())
        required(polku.tiedotPerustuvat, { when: ({ valueOf }) => !!valueOf(polku.kokonaisKmMaara) })
      },
      { injector: this._injector },
    )
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    muutokset(this.lomake.kokonaisKmMaara)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this._tiedotPerustuvatPoissa.set(!value)
        // Käytöstilan vaihto lähettää valinnan arvon tilaajille
        asetaArvo(this.lomake.tiedotPerustuvat, this.lomake.tiedotPerustuvat().value())
        this._ajopaivakirja.kokonaisKmMaara = this._validationService.processValue(value)
      })
    muutokset(this.lomake.tyoAjoissaKm)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this._ajopaivakirja.tyoAjoissaKm = this._validationService.processValue(value)
      })
    muutokset(this.lomake.tiedotPerustuvat)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this._ajopaivakirja.tiedotPerustuvat = this._validationService.processValue(value)
      })
    muutokset(this.lomake.yli10hKotimaanMatkojenLukumaara)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this._ajopaivakirja.yli10hKotimaanMatkojenLukumaara = this._validationService.processValue(value)
      })
    muutokset(this.lomake.yli6hKotimaanMatkojenLukumaara)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this._ajopaivakirja.yli6hKotimaanMatkojenLukumaara = this._validationService.processValue(value)
      })
    muutokset(this.lomake.ulkomaanMatkapaivienLukumaara)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this._ajopaivakirja.ulkomaanMatkapaivienLukumaara = this._validationService.processValue(value)
      })
    muutokset(this.lomake.ulkomaanPaivarahojenYhteismaara)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this._ajopaivakirja.ulkomaanPaivarahojenYhteismaara = this._validationService.processValue(value)
      })
    muutokset(this.lomake.kotiYrityksenTyotilana)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this._ajopaivakirja.kotiYrityksenTyotilana = this._validationService.processValue(value)
      })

    this._firebaseLemonaid
      .firestoreDoc<Ajopaivakirja>('customers/' + this.asiakasAvain + '/customer-ajopaivakirja/' + this.asiakasAvain + '_2019')
      .listen()
      .pipe(
        tap(() => this._ladataanService.aloitaLataaminen()),
        map(apk => {
          if (apk) {
            return apk
          }
          const blankApk: Ajopaivakirja = this.createBlankApk()
          return blankApk
        }),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(
        apk => {
          this._ladataanService.lopetaLataaminen()
          this._ajopaivakirja = this._copyService.cloneObjectDeep(apk)
          this.alustaLomakkeenTiedot(this._ajopaivakirja)
        },
        error => {
          this._errorHandler.handleError(error)
        },
      )
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string) {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  naytaVirhe<T>(kentta: FieldTree<T>, laji: string) {
    const tila = kentta()
    return this.onkoVirhe(kentta, laji) && (tila.dirty() || tila.touched())
  }

  async tallenna() {
    if (this._saveStarted) {
      return
    }
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }
    if (!this.asiakasAvain) {
      this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
      return
    }

    // Show loading indicator
    this._saveStarted = true
    this._ladataanService.aloitaLataaminen()

    try {
      const batch = this._firebaseLemonaid.firestoreBatch()

      const now = this._timestampService.now()
      this._ajopaivakirja.luotu = this._ajopaivakirja.luotu || now
      this._ajopaivakirja.paivitetty = now

      const kirjanpitajaTiedot = await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()
      this._ajopaivakirja.luoja = this._ajopaivakirja.luoja || kirjanpitajaTiedot.uid
      this._ajopaivakirja.paivittaja = kirjanpitajaTiedot.uid

      // Set disabled field as null
      if (this.lomake.tiedotPerustuvat().disabled()) {
        asetaArvo(this.lomake.tiedotPerustuvat, null)
        this._ajopaivakirja.tiedotPerustuvat = null
      }
      // Calculate according to kokonaismäärä and tyoajomäärä
      // this._ajopaivakirja.onkoTyoajoissaYliPuoli = this.laskeOnkoTyoajoissaYliPuoli(this._ajopaivakirja)
      // Save main doc
      batch.set('customers/' + this.asiakasAvain + '/customer-ajopaivakirja/' + this.asiakasAvain + '_2019', this._ajopaivakirja)

      // Create history entry
      const historyCopy = this._copyService.cloneObjectDeep(this._ajopaivakirja)
      const historyAvain = this._firebaseLemonaid.firestoreCreateId()
      historyCopy.avain = historyAvain
      batch.set('customers/' + this.asiakasAvain + '/customer-ajopaivakirja/' + this.asiakasAvain + '_2019' + '/customer-ajopaivakirja-history/' + historyAvain, historyCopy)

      // Commit batch
      await batch.commit()

      // All is good, let's advance
      this._snackbar.open('Tallennus onnistui', 'OK', { duration: 5000, verticalPosition: 'top' })
      // Stop loading indicator
      this._ladataanService.lopetaLataaminen()
      this._saveStarted = false
    } catch (error) {
      this._saveStarted = false
      this._ladataanService.lopetaLataaminen()
      this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
      this._errorHandler.handleError(error)
    }
  }

  private alustaLomakkeenTiedot(apk: Ajopaivakirja) {
    // // NB! Don't change order for these - timing issue with subscriptions
    asetaArvo(this.lomake.tiedotPerustuvat, apk.tiedotPerustuvat ?? null)
    asetaArvo(this.lomake.kokonaisKmMaara, apk.kokonaisKmMaara ?? null)

    asetaArvo(this.lomake.tyoAjoissaKm, apk.tyoAjoissaKm ?? null)
    asetaArvo(this.lomake.yli10hKotimaanMatkojenLukumaara, apk.yli10hKotimaanMatkojenLukumaara ?? null)
    asetaArvo(this.lomake.yli6hKotimaanMatkojenLukumaara, apk.yli6hKotimaanMatkojenLukumaara ?? null)
    asetaArvo(this.lomake.ulkomaanMatkapaivienLukumaara, apk.ulkomaanMatkapaivienLukumaara ?? null)
    asetaArvo(this.lomake.ulkomaanPaivarahojenYhteismaara, apk.ulkomaanPaivarahojenYhteismaara ?? null)
    asetaArvo(this.lomake.kotiYrityksenTyotilana, apk.kotiYrityksenTyotilana ?? null)
  }

  private createBlankApk(): Ajopaivakirja {
    const blank: Ajopaivakirja = {
      asiakasAvain: null,
      avain: null,
      luoja: null,
      luotu: null,
      paivitetty: null,
      paivittaja: null,
      poistettu: false,
      kokonaisKmMaara: null,
      tyoAjoissaKm: null,
      onkoTyoajoissaYliPuoli: null,
      tiedotPerustuvat: null,
      kotiYrityksenTyotilana: null,
      yli10hKotimaanMatkojenLukumaara: null,
      yli6hKotimaanMatkojenLukumaara: null,
      ulkomaanMatkapaivienLukumaara: null,
      ulkomaanPaivarahojenYhteismaara: null,
    }
    return blank
  }
  private tooLargeTyoAjoissaKmValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (control.value) {
      const tyoajoissaKm = control.value
      // Työajot validoidaan vain niiden muuttuessa, ei kokonaismäärän muuttuessa
      const kokonaisKm = untracked(() => this.lomake.kokonaisKmMaara().value())
      if (!kokonaisKm || tyoajoissaKm > kokonaisKm) {
        return { toolarge: true }
      }
      return null
    }
    return null
  }
  // private laskeOnkoTyoajoissaYliPuoli(ajopaivakirja: Ajopaivakirja): boolean {
  //   // if (ajopaivakirja.kokonaisKmMaara) {
  //   //   if (ajopaivakirja.tyoAjoissaKm) {
  //   //     return ajopaivakirja.tyoAjoissaKm / ajopaivakirja.kokonaisKmMaara * 100 > 50
  //   //   }
  //   //   return false
  //   // }
  //   return null
  // }
}
